import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  Checkbox,
  Paper,
  styled,
  InputLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CategorySelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  size = "small",
  fullWidth = true,
  sx = {},
  isSearchable = false,
  isMultiSelect = true, // ✅ new
  showAllOption = true,
  disabled = false,
  onOpen,
}) => {
  const [resetKey, setResetKey] = useState(0);
  const allOption = { label: "All", value: "ALL" };
  const updatedOptions = showAllOption ? [allOption, ...options] : options;

  const handleAutocompleteClose = () => {
    setResetKey((prev) => prev + 1);
    // onChange([]);
  };

  const CustomPaper = styled(Paper)(({ theme }) => ({
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)", // Customize shadow as needed
    borderRadius: "8px",
    marginTop: "4px",
  }));

  const handleAutocompleteChange = (event, newValue) => {
    if (!isMultiSelect) {
      onChange(newValue); // single item
      return;
    }

    if (!Array.isArray(newValue)) {
      onChange([]);
      return;
    }

    const isAllStillSelected = newValue.some(
      (val) => (val.value ?? val) === allOption.value
    );

    const wasAllSelectedBefore = value.length === options.length;
    const isAllRemovedNow = wasAllSelectedBefore && !isAllStillSelected;

    if (isAllRemovedNow) {
      onChange([]);
      return;
    }

    if (isAllStillSelected || newValue.length === options.length) {
      onChange(options);
    } else {
      onChange(newValue);
    }
  };

  const getAutocompleteValue = () => {
    if (!value || !options) return null;
    return (
      options.find(
        (option) => (option.value ?? option) === (value.value ?? value)
      ) ?? null
    );
  };

  const controlHeight = sx?.height ?? "44px";

  const commonInputStyles = {
    fontWeight: 500,
    height: controlHeight,
    borderRadius: "8px",
    backgroundColor: "#fff",

    "& .MuiOutlinedInput-root": {
      height: controlHeight,
      paddingRight: "36px",
      borderRadius: "8px",

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E1E1E1",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E1E1E1",
      },
      "&.Mui-focused": {
        boxShadow: "none",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E1E1E1",
      borderWidth: "1px",
      borderRadius: "8px",
    },
    "& .MuiInputBase-input": {
      padding: "10px 12px",
      color: value ? "#000000" : "black",
      fontWeight: 500,
    },
  };

  if (isSearchable) {
    return (
      <Autocomplete
        disabled={disabled}
        key={resetKey}
        multiple={!!isMultiSelect}
        // disableCloseOnSelect
        options={updatedOptions}
        disableCloseOnSelect={!!isMultiSelect} // ✅ stays open only for multi-select
        closeOnSelect={!isMultiSelect}
        // value={value || []}
        // value={
        //   Array.isArray(value) && value.length === options.length
        //     ? [allOption, ...value]
        //     : Array.isArray(value)
        //     ? value
        //     : []
        // }
        value={!!isMultiSelect ? (Array.isArray(value) ? value : []) : value}
        onChange={(event, newValue) => onChange(newValue)}
        // onChange={handleAutocompleteChange}
        onClose={handleAutocompleteClose}
        getOptionLabel={(option) => option.label ?? option}
        // getOptionLabel={(option) => {
        //   if (typeof option === "string") return option;
        //   if (typeof option === "object") {
        //     return option.label || option.name || option.value || "";
        //   }
        //   return "";
        // }}
        isOptionEqualToValue={(option, val) =>
          (option.value ?? option) === (val?.value ?? val)
        }
        popupIcon={<ExpandMoreIcon />}
        fullWidth={fullWidth}
        PaperComponent={CustomPaper}
        sx={{ ...sx }}
        renderTags={() => null}
        ListboxProps={{
          sx: {
            maxHeight: "200px",
            overflowY: "auto",
            padding: 0,
          },
        }}
        onOpen={onOpen}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value ?? option} style={{ padding: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: "6px",
                py: "4px",
                fontWeight: 400,
                position: "relative",
                width: "100%",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 12,
                  right: 12,
                  // borderBottom: index !== options.length - 1 ? '1px solid #E1E1E1' : 'none',
                  borderBottom: "1px solid #E1E1E1",
                },
              }}
            >
              <Checkbox
                checked={selected}
                sx={{
                  color: "#3F6CFF",
                  "&.Mui-checked": {
                    color: "#007BFF",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />

              {option.label ?? option}
            </Box>
          </li>
        )}
        // getOptionDisabled={(option) => option.disabled}
        // renderOption={(props, option, { selected }) => (
        //   <li
        //     {...props}
        //     key={option.value ?? option}
        //     style={{
        //       padding: 0,
        //       pointerEvents: option.disabled ? "none" : "auto", // disable pointer
        //       opacity: option.disabled ? 0.5 : 1, // reduce opacity
        //     }}
        //   >
        //     <Box
        //       sx={{
        //         display: "flex",
        //         alignItems: "center",
        //         px: "12px",
        //         py: "6px",
        //         fontWeight: 400,
        //         position: "relative",
        //         width: "100%",
        //         "&::after": {
        //           content: '""',
        //           position: "absolute",
        //           bottom: 0,
        //           left: 12,
        //           right: 12,
        //           borderBottom: "1px solid #E1E1E1",
        //         },
        //       }}
        //     >
        //       {isMultiSelect && (
        //         <Checkbox
        //           checked={selected}
        //           disabled={option.disabled} // ✅ disable checkbox too
        //           sx={{
        //             color: "#3F6CFF",
        //             "&.Mui-checked": {
        //               color: "#007BFF",
        //             },
        //             "&:hover": {
        //               backgroundColor: "transparent",
        //             },
        //           }}
        //         />
        //       )}

        //       {option.label ?? option}
        //     </Box>
        //   </li>
        // )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            size={size}
            sx={commonInputStyles}
          />
        )}
      />
    );
  }

  return (
    <FormControl fullWidth={fullWidth} size={size} sx={{ mt: 0, mb: 0, ...sx }}>
      <Select
        // value={value || ""}
        value={value?.value || value || ""}
        onChange={(e) => onChange(e.target.value)}
        IconComponent={ExpandMoreIcon}
        disabled={disabled}
        displayEmpty
        inputProps={{
          MenuProps: {
            disableScrollLock: true,
            PaperProps: {
              sx: {
                borderRadius: "8px",
                paddingX: "8px",
                paddingY: "8px",
                maxHeight: "200px",
                overflowY: "auto",
              },
            },
            MenuListProps: {
              sx: {
                paddingTop: 0,
                paddingBottom: 0,
              },
            },
          },
        }}
        sx={{
          fontWeight: 400,
          height: controlHeight,
          borderRadius: "8px",
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-root": {
            height: controlHeight,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E1E1E1",
            borderWidth: "1px",
            borderRadius: "8px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E1E1E1",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E1E1E1",
            borderWidth: "1px",
          },
          "&.Mui-focused": {
            boxShadow: "none",
          },
          "& .MuiSelect-select": {
            padding: "10px 12px",
            color: value ? "#000000" : "#7A7A7A",
          },
          "& .Mui-selected": {
            backgroundColor: "transparent !important",
          },
        }}
      >
        {!value && (
          <MenuItem
            value=""
            disabled
            sx={{
              borderBottom: "1px solid #E1E1E1",
              paddingY: "10px",
              paddingX: "12px",
              color: "#7A7A7A",
              fontWeight: 500,
              display: "none",
            }}
          >
            {placeholder || "Select..."}
          </MenuItem>
        )}

        {options.map((option, index) => (
          <MenuItem
            key={option.value ?? option}
            value={option.value ?? option}
            sx={{
              borderBottom:
                index !== options.length - 1 ? "1px solid #E1E1E1" : "none",
              paddingY: "10px",
              paddingX: "12px",
              fontWeight: 400,
            }}
          >
            {option.label ?? option}
          </MenuItem>
        ))}

        {/* {options.map((option, index) => (
          <MenuItem
            key={option.value ?? option}
            value={option.value ?? option}
            disabled={option.disabled} // ✅ native MUI support
            sx={{
              borderBottom:
                index !== options.length - 1 ? "1px solid #E1E1E1" : "none",
              paddingY: "10px",
              paddingX: "12px",
              fontWeight: 400,
              opacity: option.disabled ? 0.5 : 1, // ✅ faded style
              pointerEvents: option.disabled ? "none" : "auto", // ✅ disable mouse
            }}
          >
            {option.label ?? option}
          </MenuItem>
        ))} */}
      </Select>
    </FormControl>
  );
};

export default CategorySelect;
